import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, Download, BookOpen, MessageSquare, Hash, Tag } from "lucide-react";

const INPUT_CLS = "w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] text-[var(--text-primary)]";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] font-mono text-[var(--text-muted)] mt-1">{hint}</div>}
    </div>
  );
}

const CSV_TEMPLATE = `subreddit,thread_title,thread_body,comment_1,comment_2,comment_3,tags,source_url
r/india,"is it just me or WFH from PG is actually peaceful","been WFH from my PG in Indore since Aug. no traffic, ghar ka khana... anyone else?","bhai same, i moved back to Bhopal","lol i tried and got kicked out because my landlord thought i was building a call center","yaar this is so relatable, sarafa bazaar in the evening after work >>","wfh;indore;pg;relatable",https://reddit.com/r/india/comments/xxx
r/developersIndia,"25L CTC in bangalore vs 18L in pune, help","offer letters came in yesterday and i'm losing sleep. cost of living difference is real...",bengaluru weather is trash tho,"bhai 25L in bangalore = 18L in pune after everything. take pune",traffic in bangalore will make you regret,ctc;bangalore;pune;job-decision,https://reddit.com/r/developersIndia/comments/yyy
`;

function SampleForm({ onSaved }) {
  const [form, setForm] = useState({
    subreddit: "", thread_title: "", thread_body: "",
    comments: [""], tags: "", source_url: "",
  });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setComment = (i) => (e) => setForm((p) => ({
    ...p, comments: p.comments.map((c, j) => j === i ? e.target.value : c),
  }));

  const save = useMutation({
    mutationFn: (body) => api.post("/training/samples", body),
    onSuccess: () => { toast.success("Sample saved"); onSaved?.(); setForm({subreddit:"",thread_title:"",thread_body:"",comments:[""],tags:"",source_url:""}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const submit = () => {
    if (!form.thread_title.trim()) { toast.error("Title required"); return; }
    save.mutate({
      subreddit: form.subreddit,
      thread_title: form.thread_title,
      thread_body: form.thread_body,
      comments: form.comments.filter(c => c && c.trim()),
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      source_url: form.source_url,
    });
  };
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-4 sm:p-6 space-y-4">
      <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight">Add a sample.</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Subreddit"><input className={`${INPUT_CLS} font-mono`} placeholder="r/india" data-testid="training-subreddit" value={form.subreddit} onChange={set("subreddit")}/></Field>
        <Field label="Source URL (optional)"><input className={`${INPUT_CLS} font-mono`} placeholder="https://reddit.com/..." data-testid="training-source-url" value={form.source_url} onChange={set("source_url")}/></Field>
      </div>
      <Field label="Thread title *">
        <input className={INPUT_CLS} data-testid="training-title"
               value={form.thread_title} onChange={set("thread_title")}
               placeholder="paste the real reddit post title verbatim"/>
      </Field>
      <Field label="Thread body">
        <textarea rows={4} className={INPUT_CLS} data-testid="training-body"
                  value={form.thread_body} onChange={set("thread_body")}
                  placeholder="the actual body of the post — keep typos, slang, everything"/>
      </Field>
      <Field label={`Top comments (${form.comments.filter(c => c.trim()).length})`}>
        <div className="space-y-2">
          {form.comments.map((c, i) => (
            <textarea key={`comment-${i}`} rows={2} className={INPUT_CLS} data-testid={`training-comment-${i}`}
                      value={c} onChange={setComment(i)}
                      placeholder={`comment #${i+1} — verbatim`}/>
          ))}
          <button onClick={() => setForm(p => ({...p, comments: [...p.comments, ""]}))}
                  data-testid="training-add-comment"
                  className="text-[11px] font-mono uppercase tracking-widest border border-[var(--border)] hover:border-[var(--border-strong)] px-3 py-1.5 flex items-center gap-2">
            <Plus className="w-3 h-3"/> Add comment
          </button>
        </div>
      </Field>
      <Field label="Tags (comma-separated)" hint="Used for retrieval — e.g. wfh, salary, engineering">
        <input className={INPUT_CLS} data-testid="training-tags" value={form.tags} onChange={set("tags")}/>
      </Field>
      <button onClick={submit} disabled={save.isPending} data-testid="training-save-btn"
              className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
        {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
        SAVE SAMPLE
      </button>
    </div>
  );
}

function CsvImport({ onImported }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/training/csv-import", form, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Imported ${r.data.inserted} · Skipped ${r.data.skipped}`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const download = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "threadops-training-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-4 sm:p-6 space-y-3">
      <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight">Bulk import (CSV).</h3>
      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        Columns: <code className="font-mono">subreddit, thread_title, thread_body, comment_1, comment_2, ..., tags, source_url</code>. Only <b>thread_title</b> is required. Multiple comment columns are auto-joined. Max 5MB.
      </p>
      <input ref={inputRef} type="file" accept=".csv" data-testid="training-csv-input"
             className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={() => inputRef.current?.click()} disabled={busy}
                data-testid="training-csv-upload-btn"
                className="flex-1 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} UPLOAD CSV
        </button>
        <button onClick={download} data-testid="training-csv-template-btn"
                className="border border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--text-secondary)] px-4 py-2.5 flex items-center justify-center gap-2 text-[12px]">
          <Download className="w-4 h-4"/> Template
        </button>
      </div>
    </div>
  );
}

export default function Training() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["training-samples"],
    queryFn: () => api.get("/training/samples?limit=200").then(r => r.data),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/training/samples/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({queryKey:["training-samples"]}); },
  });
  const refresh = () => qc.invalidateQueries({queryKey:["training-samples"]});

  return (
    <Layout title="Training corpus." subtitle="Teach Jarvis how real Reddit sounds">
      <div className="mb-6 border border-blue-500/25 bg-blue-500/5 p-4 flex gap-3 items-start" data-testid="training-note">
        <BookOpen className="w-4 h-4 text-blue-400 shrink-0 mt-0.5"/>
        <div className="text-[12px] text-blue-200 leading-relaxed">
          Upload real threads + comments — verbatim, with all slang & typos.
          For now these are <b>stored</b> and browsable; fine-tuning-style batch job is on the roadmap.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <SampleForm onSaved={refresh}/>
        <CsvImport onImported={refresh}/>
      </div>

      <div className="mb-3 flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          // corpus · {q.data?.total ?? 0} samples
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {q.isLoading && <div className="col-span-2 text-[var(--text-muted)] text-sm p-6">Loading…</div>}
        {q.data?.items?.length === 0 && !q.isLoading && (
          <div className="col-span-2 border border-dashed border-[var(--border)] p-12 text-center bg-dots" data-testid="training-empty">
            <BookOpen className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-4 opacity-40"/>
            <div className="text-[var(--text-secondary)] text-sm">No samples yet. Add one on the left or bulk-import a CSV.</div>
          </div>
        )}
        {q.data?.items?.map((s) => (
          <div key={s.id} data-testid={`training-sample-${s.id}`}
               className="border border-[var(--border)] bg-[var(--surface)] p-4 hover:border-[var(--border-strong)] transition-colors">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {s.subreddit && <span className="font-mono text-[10px] text-[var(--accent)]"><Hash className="w-3 h-3 inline"/> {s.subreddit.replace(/^r\//,"")}</span>}
                  {s.tags?.map((t,i) => (
                    <span key={`tag-${s.id}-${i}-${t}`} className="font-mono text-[9px] uppercase tracking-widest text-[var(--text-muted)]"><Tag className="w-2.5 h-2.5 inline"/> {t}</span>
                  ))}
                </div>
                <div className="text-[14px] font-medium text-[var(--text-primary)] leading-snug">{s.thread_title}</div>
                {s.thread_body && (
                  <div className="text-[12px] text-[var(--text-muted)] mt-2 line-clamp-3">{s.thread_body}</div>
                )}
                {s.comments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--border)] space-y-1.5">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-1">
                      <MessageSquare className="w-2.5 h-2.5"/> {s.comments.length} comment{s.comments.length !== 1 ? "s" : ""}
                    </div>
                    {s.comments.slice(0,2).map((c,i) => (
                      <div key={`c-${s.id}-${i}`} className="text-[11px] text-[var(--text-secondary)] italic border-l-2 border-[var(--border)] pl-2">
                        {c.slice(0,180)}{c.length > 180 ? "…" : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => del.mutate(s.id)}
                data-testid={`training-delete-${s.id}`}
                className="shrink-0 p-1.5 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--text-muted)] hover:text-[var(--accent)]">
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
